import React, {useCallback, useEffect, useState} from 'react';
import {useParams, useNavigate} from 'react-router-dom';
import { Col, Flex, Input, Modal, Row, Skeleton, Space, Steps, Tag, Typography, message} from 'antd';
import {ArrowLeftOutlined, EnvironmentOutlined, ShoppingOutlined} from '@ant-design/icons';
import styled from 'styled-components';
import ENDPOINTS from '../../constants/endpoints';
import {formatPath, ROUTES} from '../../constants/PathRoutes';
import {moneyMask, dateFormat} from '../../utils/utils';
import {BORDER_GRAY, GREE_1, ORDER_STATUS_COLOR, WHITE} from '../../utils/colors';
import api from '../../services/api';
import Button from "../../components/data_entry/Button";

const Section = styled.div`
    background: ${WHITE};
    border: 1px solid ${BORDER_GRAY};
    border-radius: 10px;
    padding: 20px 24px;
    margin-bottom: 16px;
`;

const ItemRow = styled.div`
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 10px 0;
    &:not(:last-child) {
        border-bottom: 1px solid #f0f0f0;
    }
`;

const ItemImage = styled.img`
    width: 52px;
    height: 52px;
    object-fit: cover;
    border-radius: 8px;
    flex-shrink: 0;
`;

const ImagePlaceholder = styled.div`
    width: 52px;
    height: 52px;
    border-radius: 8px;
    background: #f0f0f0;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    color: #ccc;
    font-size: 20px;
`;

const STATUS_STEPS = [
    {key: 'PAID', title: 'Pagamento'},
    {key: 'PREPARING', title: 'Em Preparo'},
    {key: 'SHIPPED', title: 'Enviado'},
    {key: 'DELIVERED', title: 'Entregue'},
];

const STATUS_ORDER = ['PENDING_PAYMENT', 'PAID', 'PREPARING', 'SHIPPED', 'DELIVERED'];

const STATUS_NEXT = {
    PAID: 'PREPARING',
    PREPARING: 'SHIPPED',
    SHIPPED: 'DELIVERED',
};

const STATUS_NEXT_LABEL = {
    PAID: 'Marcar como Em Preparo',
    PREPARING: 'Marcar como Enviado',
    SHIPPED: 'Marcar como Entregue',
};

function getSteps(status) {
    const isPending = status === 'PENDING_PAYMENT';
    return STATUS_STEPS.map(s => ({
        ...s,
        title: s.key === 'PAID' && isPending ? 'Aguardando Pagamento' : s.title,
    }));
}

function getStepCurrent(status) {
    const orderIdx = STATUS_ORDER.indexOf(status);
    return Math.max(0, orderIdx - 1);
}

export default function AdminOrderDetail() {
    const {id} = useParams();
    const navigate = useNavigate();
    const [order, setOrder] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [updating, setUpdating] = useState(false);
    const [trackingModal, setTrackingModal] = useState(false);
    const [trackingCode, setTrackingCode] = useState('');

    const fetchOrder = useCallback(async () => {
        setIsLoading(true);
        try {
            const res = await api.get(formatPath(ENDPOINTS.ECOMMERCE_STAFF_ORDER_DETAIL, {id}));
            setOrder(res.data);
        } finally {
            setIsLoading(false);
        }
    }, [id]);

    useEffect(() => {
        fetchOrder();
    }, [fetchOrder]);

    const handleUpdateStatus = () => {
        const nextStatus = STATUS_NEXT[order.status];
        if (!nextStatus) return;
        if (nextStatus === 'SHIPPED') {
            setTrackingCode('');
            setTrackingModal(true);
        } else {
            submitStatus(nextStatus);
        }
    };

    const submitStatus = async (nextStatus, extraBody = {}) => {
        setUpdating(true);
        try {
            await api.post(formatPath(ENDPOINTS.ECOMMERCE_STAFF_ORDER_UPDATE_STATUS, {id}), {status: nextStatus, ...extraBody});
            message.success('Status atualizado com sucesso');
            await fetchOrder();
        } catch {
            message.error('Erro ao atualizar status');
        } finally {
            setUpdating(false);
        }
    };

    const handleTrackingConfirm = () => {
        if (!trackingCode.trim()) {
            message.warning('Informe o código de rastreio');
            return;
        }
        setTrackingModal(false);
        submitStatus('SHIPPED', {tracking_code: trackingCode.trim()});
    };

    if (isLoading || !order) {
        return (
            <Section>
                <Skeleton active paragraph={{rows: 6}}/>
            </Section>
        );
    }

    const hasDiscount = order.discount_total && parseFloat(order.discount_total) > 0;
    const isCancelled = order.status === 'CANCELLED';
    const stepCurrent = getStepCurrent(order.status);
    const nextStatusLabel = STATUS_NEXT_LABEL[order.status];

    return (
        <>
            <Space style={{marginBottom: 20}}>
                <Button
                    type="text"
                    icon={<ArrowLeftOutlined/>}
                    onClick={() => navigate(ROUTES.ADMIN_ECOMMERCE_ORDER_LIST)}
                    style={{padding: 0, color: GREE_1}}
                >
                    Pedidos
                </Button>
            </Space>

            {/* Header */}
            <Section>
                <Row justify="space-between" align="middle" wrap>
                    <Col>
                        <Space direction="vertical" size={2}>
                            <Typography.Title level={5} style={{margin: 0}}>
                                {order.order_number}
                            </Typography.Title>
                            <Typography.Text type="secondary" style={{fontSize: 12}}>
                                Realizado em {dateFormat(order.created_at)}
                            </Typography.Text>
                        </Space>
                    </Col>
                    <Col>
                        <Space>
                            <Tag color={ORDER_STATUS_COLOR[order.status] ?? 'default'} style={{fontSize: 13, padding: '4px 10px'}}>
                                {order.status_display}
                            </Tag>
                            {nextStatusLabel && (
                                <Button
                                    loading={updating}
                                    onClick={handleUpdateStatus}
                                >
                                    {nextStatusLabel}
                                </Button>
                            )}
                        </Space>
                    </Col>
                </Row>
            </Section>

            {/* Ciclo de vida */}
            <Section>
                {isCancelled ? (
                    <Space>
                        <Tag color="red" style={{fontSize: 13, padding: '4px 10px'}}>Pedido Cancelado</Tag>
                        <Typography.Text type="secondary" style={{fontSize: 13}}>
                            Este pedido foi cancelado.
                        </Typography.Text>
                    </Space>
                ) : (
                    <Steps
                        current={stepCurrent}
                        items={getSteps(order.status).map(s => ({title: s.title}))}
                        style={{overflowX: 'auto'}}
                    />
                )}
                {order.tracking_code && (
                    <div style={{marginTop: 16}}>
                        <Typography.Text type="secondary">Código de Rastreio: </Typography.Text>
                        <Typography.Text strong copyable>{order.tracking_code}</Typography.Text>
                    </div>
                )}
            </Section>

            <Modal
                title="Código de Rastreio"
                open={trackingModal}
                onOk={handleTrackingConfirm}
                onCancel={() => setTrackingModal(false)}
                okText="Confirmar"
                cancelText="Cancelar"
                confirmLoading={updating}
            >
                <Space direction="vertical" style={{width: '100%', marginTop: 8}}>
                    <Typography.Text>Informe o código de rastreio para marcar o pedido como Enviado:</Typography.Text>
                    <Input
                        placeholder="Ex: BR123456789BR"
                        value={trackingCode}
                        onChange={e => setTrackingCode(e.target.value)}
                        onPressEnter={handleTrackingConfirm}
                        autoFocus
                    />
                </Space>
            </Modal>

            <Row gutter={16} align="stretch">
                {/* Endereço */}
                <Col xs={24} md={12} style={{display: 'flex', flexDirection: 'column'}}>
                    <Section style={{flex: 1}}>
                        <Flex vertical>
                            <Space style={{marginBottom: 10}}>
                                <EnvironmentOutlined style={{color: GREE_1}}/>
                                <Typography.Text strong>Endereço de Entrega</Typography.Text>
                            </Space>
                            <Space direction="vertical" size={2}>
                                <Typography.Text>
                                    {order.delivery_street}, {order.delivery_number}
                                    {order.delivery_complement ? ` - ${order.delivery_complement}` : ''}
                                </Typography.Text>
                                <Typography.Text type="secondary">{order.delivery_neighborhood}</Typography.Text>
                                <Typography.Text type="secondary">
                                    {order.delivery_city} / {order.delivery_state}
                                </Typography.Text>
                                <Typography.Text type="secondary">CEP {order.delivery_postal_code}</Typography.Text>
                            </Space>
                        </Flex>
                    </Section>
                </Col>

                {/* Valores */}
                <Col xs={24} md={12} style={{display: 'flex', flexDirection: 'column'}}>
                    <Section style={{flex: 1}}>
                        <Typography.Text strong style={{display: 'block', marginBottom: 10}}>Resumo</Typography.Text>
                        <Space direction="vertical" size={6} style={{width: '100%'}}>
                            {hasDiscount && (
                                <>
                                    <Row justify="space-between">
                                        <Typography.Text type="secondary">Subtotal</Typography.Text>
                                        <Typography.Text>R$ {moneyMask(order.subtotal)}</Typography.Text>
                                    </Row>
                                    <Row justify="space-between">
                                        <Typography.Text type="secondary">Desconto</Typography.Text>
                                        <Typography.Text style={{color: '#cf1322'}}>
                                            - R$ {moneyMask(order.discount_total)}
                                        </Typography.Text>
                                    </Row>
                                </>
                            )}
                            <Row justify="space-between">
                                <Typography.Text type="secondary">Frete</Typography.Text>
                                <Typography.Text>R$ {moneyMask(order.shipping_cost)}</Typography.Text>
                            </Row>
                            <Row justify="space-between" style={{borderTop: `1px solid ${BORDER_GRAY}`, paddingTop: 8, marginTop: 4}}>
                                <Typography.Text strong>Total</Typography.Text>
                                <Typography.Text strong style={{color: GREE_1, fontSize: 16}}>
                                    R$ {moneyMask(order.total)}
                                </Typography.Text>
                            </Row>
                        </Space>
                    </Section>
                </Col>
            </Row>

            {/* Itens */}
            <Section>
                <Typography.Text strong style={{fontSize: 14, display: 'block', marginBottom: 12}}>
                    Itens do Pedido
                </Typography.Text>
                {order.items.map(item => (
                    <ItemRow key={item.id}>
                        {item.product_image
                            ? <ItemImage src={item.product_image} alt={item.product_name}/>
                            : <ImagePlaceholder><ShoppingOutlined/></ImagePlaceholder>
                        }
                        <div style={{flex: 1, minWidth: 0}}>
                            <Typography.Text strong style={{display: 'block'}}>
                                {item.product_name}
                            </Typography.Text>
                            <Typography.Text type="secondary" style={{fontSize: 12}}>
                                {item.quantity}x · R$ {moneyMask(item.unit_price)}
                                {item.discount_percentage && (
                                    <span style={{color: '#fa8c16', marginLeft: 6}}>
                                        -{item.discount_percentage}% receita
                                    </span>
                                )}
                            </Typography.Text>
                        </div>
                        <Typography.Text strong style={{color: GREE_1, whiteSpace: 'nowrap'}}>
                            R$ {moneyMask(item.subtotal)}
                        </Typography.Text>
                    </ItemRow>
                ))}
            </Section>
        </>
    );
}
